import {
  ArrayField,
  ArrayInput,
  BooleanInput,
  Button,
  Create,
  Datagrid,
  DateField,
  DateInput,
  Edit,
  EditButton,
  FunctionField,
  Filter,
  ImageField,
  ImageInput,
  List,
  ListContextProvider,
  NumberInput,
  ReferenceManyField,
  RichTextField,
  Show,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  WrapperField,
  useGetList,
  useGetMany,
  useList,
  useNotify,
  useRecordContext,
} from "react-admin";
import {
  Box,
  Button as MuiButton,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { EventInstanceCreator } from "../components/EventInstanceCreator";
import { InstanceSelector } from "../components/InstanceSelector";
import { InstanceSalesView, toCsv } from "../components/EventTicketSales";
import { RichTextInput } from "ra-input-rich-text";
import { centsToCurrency } from "../utils";

const CreateEventInstanceButton = () => {
  const record = useRecordContext<{ id: string; name: string }>();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button onClick={handleClick} label="Add Instances" variant="contained" />
      <EventInstanceCreator event={record} open={open} onClose={handleClose} />
    </>
  );
};

const transformEventData = (data: any) => {
  if (data.additionalImages && data.additionalImages.length > 0) {
    data.additionalImages = data.additionalImages.map((image: any) => {
      if (typeof image.src === "string") {
        return image;
      }
      if (!image.src?.src) {
        return null;
      }
      return {
        src: image.src.src, // image.src is already a URL string
        title: image.title, // title should be top-level
      };
    });
  }
  return data;
};

const EventFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="Search" source="name" alwaysOn />
    <BooleanInput source="isActive" label="Active" alwaysOn />
  </Filter>
);

const toDate = (val: any): Date => {
  if (val instanceof Date) return val;
  if (val?.seconds) return new Date(val.seconds * 1000);
  return new Date(val);
};

interface EventInstance {
  id: string;
  startDate: Date;
  [key: string]: any;
}

interface EventListData {
  nextInstance: EventInstance | null;
  upcomingCount: number;
  nextInstanceSoldCount: number;
  nextInstanceRevenue: number;
}

const useEventListData = () => {
  const { data: instances, isLoading: instancesLoading } = useGetList(
    "eventInstances",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "startDate", order: "DESC" },
    }
  );

  const { data: passes, isLoading: passesLoading } = useGetList("passes", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "createdate", order: "DESC" },
  });

  const eventDataMap = useMemo(() => {
    const map = new Map<string, EventListData>();
    if (!instances) return map;

    const now = new Date();

    // Group instances by eventId
    const instancesByEvent = new Map<string, any[]>();
    instances.forEach((inst) => {
      if (!instancesByEvent.has(inst.eventId)) {
        instancesByEvent.set(inst.eventId, []);
      }
      instancesByEvent.get(inst.eventId)!.push(inst);
    });

    // Group passes by eventInstanceId
    const passesByInstance = new Map<string, any[]>();
    (passes || []).forEach((pass) => {
      const iid = pass.eventInstanceId;
      if (iid) {
        if (!passesByInstance.has(iid)) {
          passesByInstance.set(iid, []);
        }
        passesByInstance.get(iid)!.push(pass);
      }
    });

    // Aggregate per event
    instancesByEvent.forEach((eventInstances, eventId) => {
      const upcoming = eventInstances
        .filter((i) => toDate(i.startDate) > now)
        .sort(
          (a, b) =>
            toDate(a.startDate).getTime() - toDate(b.startDate).getTime()
        );

      const nextInstance = upcoming[0] || null;

      const nextPasses = nextInstance
        ? passesByInstance.get(nextInstance.id) || []
        : [];

      map.set(eventId, {
        nextInstance,
        upcomingCount: upcoming.length,
        nextInstanceSoldCount: nextPasses.length,
        nextInstanceRevenue: nextPasses.reduce(
          (sum, p) => sum + (p.amount || 0),
          0
        ),
      });
    });

    return map;
  }, [instances, passes]);

  return {
    eventDataMap,
    isLoading: instancesLoading || passesLoading,
  };
};

const EventStatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Chip
      label={record.isActive ? "Active" : "Inactive"}
      size="small"
      color={record.isActive ? "success" : "default"}
      variant={record.isActive ? "filled" : "outlined"}
    />
  );
};

const NextDateField = ({
  eventDataMap,
}: {
  eventDataMap: Map<string, EventListData>;
}) => {
  const record = useRecordContext();
  if (!record) return null;

  const data = eventDataMap.get(record.id.toString());
  if (!data?.nextInstance) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        No upcoming
      </Typography>
    );
  }

  const date = toDate(data.nextInstance.startDate);
  const formatted = format(date, "MMM d, yyyy h:mm a");
  const extra = data.upcomingCount > 1 ? ` (+${data.upcomingCount - 1} more)` : "";

  return (
    <Box>
      <Typography variant="body2">{formatted}</Typography>
      {extra && (
        <Typography variant="caption" color="text.secondary">
          {extra}
        </Typography>
      )}
    </Box>
  );
};

const SalesField = ({
  eventDataMap,
}: {
  eventDataMap: Map<string, EventListData>;
}) => {
  const record = useRecordContext();
  if (!record) return null;

  const data = eventDataMap.get(record.id.toString());
  if (!data?.nextInstance) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2">
        {data.nextInstanceSoldCount} sold
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {centsToCurrency(data.nextInstanceRevenue)}
      </Typography>
    </Box>
  );
};

const EventList = (props: any) => {
  const { eventDataMap } = useEventListData();

  return (
    <List {...props} filters={<EventFilter />}>
      <Datagrid
        rowClick="show"
        rowSx={(record: any) =>
          record?.isActive === false
            ? { opacity: 0.6, backgroundColor: "#f8f9fa" }
            : {}
        }
      >
        <ImageField source="mainImage.src" label="Image" />
        <WrapperField label="Event" sortBy="name">
          <TextField source="name" />
          <br />
          <EventStatusChip />
        </WrapperField>
        <WrapperField label="Next Date">
          <NextDateField eventDataMap={eventDataMap} />
        </WrapperField>
        <WrapperField label="Next Instance Sales">
          <SalesField eventDataMap={eventDataMap} />
        </WrapperField>
        <EditButton label="" />
      </Datagrid>
    </List>
  );
};

const EventLinkField = () => {
  const record = useRecordContext();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(`https://colalounge.com/events/${record.id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box display="flex" alignItems="center">
      <a href={`https://colalounge.com/events/${record.id}`}>
        colalounge.com/events/{record.id}
      </a>
      <IconButton onClick={handleCopy} size="small" sx={{ ml: 1 }}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
      {copied && (
        <Typography variant="caption" sx={{ ml: 1 }}>
          Copied!
        </Typography>
      )}
    </Box>
  );
};

const ExportAllAttendeesButton = ({
  allPasses,
  instances,
  eventName,
}: {
  allPasses: any[] | undefined;
  instances: any[] | undefined;
  eventName: string;
}) => {
  const notify = useNotify();

  const memberIds = useMemo(
    () =>
      Array.from(
        new Set((allPasses || []).map((p) => p.memberId).filter(Boolean))
      ),
    [allPasses]
  );

  const { data: membersData } = useGetMany("members", { ids: memberIds });

  const membersMap = useMemo(() => {
    const map = new Map<string, any>();
    (membersData || []).forEach((m) => map.set(m.id, m));
    return map;
  }, [membersData]);

  const handleExport = useCallback(() => {
    if (!allPasses || allPasses.length === 0) {
      notify("No attendees to export", { type: "warning" });
      return;
    }

    // Aggregate per member: total tickets + unique instances
    const memberStats: Record<
      string,
      { totalTickets: number; instanceIds: Set<string> }
    > = {};
    allPasses.forEach((pass) => {
      const mid = pass.memberId;
      if (!mid) return;
      if (!memberStats[mid]) {
        memberStats[mid] = { totalTickets: 0, instanceIds: new Set() };
      }
      memberStats[mid].totalTickets++;
      if (pass.eventInstanceId) {
        memberStats[mid].instanceIds.add(pass.eventInstanceId);
      }
    });

    const records = Object.entries(memberStats)
      .map(([memberId, stats]) => {
        const member = membersMap.get(memberId);
        const displayName = member?.displayName || "";
        const nameParts = displayName.split(" ");
        return {
          "First Name": nameParts[0] || "",
          "Last Name": nameParts.slice(1).join(" ") || "",
          Email: member?.email || "",
          Phone: member?.phoneNumber || "",
          "Events Attended": stats.instanceIds.size,
          "Total Tickets": stats.totalTickets,
        };
      })
      .sort((a, b) => a["Last Name"].localeCompare(b["Last Name"]));

    try {
      const headers = [
        "First Name",
        "Last Name",
        "Email",
        "Phone",
        "Events Attended",
        "Total Tickets",
      ];
      const csv = toCsv(headers, records);

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = eventName.replace(/[^a-zA-Z0-9-]/g, "_");
      link.download = `all_attendees_${safeName}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      notify(`Exported ${records.length} attendees`);
    } catch {
      notify("Error exporting attendees", { type: "error" });
    }
  }, [allPasses, membersMap, eventName, notify]);

  return (
    <MuiButton
      onClick={handleExport}
      variant="outlined"
      startIcon={<DownloadIcon />}
      disabled={!allPasses || allPasses.length === 0}
      size="small"
    >
      Export All Attendees
    </MuiButton>
  );
};

const EventShowContent = () => {
  const record = useRecordContext();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(
    null
  );
  const [showPast, setShowPast] = useState(false);

  const { data: instances } = useGetList("eventInstances", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "startDate", order: "ASC" },
    filter: { eventId: record?.id },
  });

  const { data: allPasses } = useGetList("passes", {
    pagination: { page: 1, perPage: 5000 },
    filter: { eventId: record?.id },
  });

  const passCountsByInstance = useMemo(() => {
    const counts: Record<string, number> = {};
    (allPasses || []).forEach((pass) => {
      const iid = pass.eventInstanceId;
      if (iid) counts[iid] = (counts[iid] || 0) + 1;
    });
    return counts;
  }, [allPasses]);

  // Auto-select next upcoming instance on load
  useEffect(() => {
    if (!instances || instances.length === 0 || selectedInstanceId) return;

    const now = new Date();
    const upcoming = instances
      .filter((i) => toDate(i.startDate) > now)
      .sort(
        (a, b) => toDate(a.startDate).getTime() - toDate(b.startDate).getTime()
      );

    if (upcoming.length > 0) {
      setSelectedInstanceId(upcoming[0].id);
    } else {
      // No upcoming — show past and select most recent
      setShowPast(true);
      const sorted = [...instances].sort(
        (a, b) => toDate(b.startDate).getTime() - toDate(a.startDate).getTime()
      );
      setSelectedInstanceId(sorted[0].id);
    }
  }, [instances, selectedInstanceId]);

  const selectedInstance = useMemo(
    () => (instances || []).find((i) => i.id === selectedInstanceId),
    [instances, selectedInstanceId]
  );

  const instanceLabel = selectedInstance
    ? format(toDate(selectedInstance.startDate), "MMM-d-yyyy")
    : "";

  // Split instances into upcoming / past for the All Instances tab
  const { upcomingInstances, pastInstances } = useMemo(() => {
    const now = new Date();
    const up: any[] = [];
    const pa: any[] = [];
    (instances || []).forEach((i) => {
      if (toDate(i.startDate) > now) up.push(i);
      else pa.push(i);
    });
    up.sort(
      (a, b) => toDate(a.startDate).getTime() - toDate(b.startDate).getTime()
    );
    pa.sort(
      (a, b) => toDate(b.startDate).getTime() - toDate(a.startDate).getTime()
    );
    return { upcomingInstances: up, pastInstances: pa };
  }, [instances]);

  const [showPastInList, setShowPastInList] = useState(false);

  if (!record) return null;

  return (
    <Box>
      {/* Event Header */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            {record.name}
          </Typography>
          <EventLinkField />
        </Box>
        <ExportAllAttendeesButton
          allPasses={allPasses}
          instances={instances}
          eventName={record.name}
        />
      </Box>

      {/* Instance Selector */}
      <InstanceSelector
        instances={instances || []}
        selectedInstanceId={selectedInstanceId}
        onSelectInstance={setSelectedInstanceId}
        showPast={showPast}
        onToggleShowPast={setShowPast}
        passCountsByInstance={passCountsByInstance}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Sales & Attendees" />
          <Tab label="Event Details" />
          <Tab label="All Instances" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <InstanceSalesView
          eventId={record.id.toString()}
          instanceId={selectedInstanceId}
          eventName={record.name}
          instanceLabel={instanceLabel}
        />
      )}

      {activeTab === 1 && (
        <Box>
          <TextField source="id" />
          <Box sx={{ my: 1 }}>
            <ImageField source="mainImage.src" label="Main Image" />
          </Box>
          <ArrayField source="additionalImages" label="Additional Images">
            <Datagrid bulkActionButtons={false}>
              <ImageField source="src" />
              <TextField source="title" />
            </Datagrid>
          </ArrayField>
          <Box sx={{ my: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Summary
            </Typography>
            <TextField source="summary" />
          </Box>
          <Box sx={{ my: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Description
            </Typography>
            <RichTextField source="description" />
          </Box>
          <Box sx={{ my: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Ticket Link
            </Typography>
            <TextField source="ticketlink" />
          </Box>
          <ArrayField source="tickets" label="Ticket Configuration">
            <Datagrid bulkActionButtons={false}>
              <TextField source="id" label="Ticket ID" />
              <TextField source="name" label="Ticket Name" />
              <TextField source="description" label="Ticket Description" />
              <TextField source="amount" label="Price in cents" />
            </Datagrid>
          </ArrayField>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {/* Upcoming Instances */}
          <Typography variant="h6" gutterBottom>
            Upcoming Instances ({upcomingInstances.length})
          </Typography>
          {upcomingInstances.length > 0 ? (
            <InstanceTable
              instances={upcomingInstances}
              passCountsByInstance={passCountsByInstance}
            />
          ) : (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No upcoming instances
            </Typography>
          )}

          {/* Past Instances */}
          <Box sx={{ mt: 3 }}>
            <Button
              onClick={() => setShowPastInList(!showPastInList)}
              label={
                showPastInList
                  ? "Hide past instances"
                  : `Show past instances (${pastInstances.length})`
              }
              size="small"
            />
            {showPastInList && pastInstances.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <InstanceTable
                  instances={pastInstances}
                  passCountsByInstance={passCountsByInstance}
                  isPast
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const InstanceTable = ({
  instances,
  passCountsByInstance,
  isPast = false,
}: {
  instances: any[];
  passCountsByInstance: Record<string, number>;
  isPast?: boolean;
}) => {
  const listContext = useList({
    data: instances,
    sort: { field: "startDate", order: isPast ? "DESC" : "ASC" },
  });
  return (
    <ListContextProvider value={listContext}>
      <Datagrid
        bulkActionButtons={false}
        rowSx={() => (isPast ? { opacity: 0.6 } : {})}
      >
        <DateField source="startDate" label="Starts At" showTime />
        <DateField source="endDate" label="Ends At" showTime />
        <TextField source="description" label="Description" />
        <FunctionField
          label="Tickets Sold"
          render={(record: any) => passCountsByInstance[record?.id] || 0}
        />
      </Datagrid>
    </ListContextProvider>
  );
};

export const EventShow = (props: any) => (
  <Show {...props}>
    <EventShowContent />
  </Show>
);

export const EventCreate = (props: any) => (
  <Create {...props} transform={transformEventData}>
    <SimpleForm>
      <TextInput
        source="id"
        label="Event Slug"
        required
        helperText="unique-name-of-event"
      />
      <TextInput source="name" label="Event Name" fullWidth />
      <BooleanInput source="isActive" />

      <ImageInput source="mainImage" label="Main Image" accept="image/*">
        <ImageField source="src" title="title" />
      </ImageInput>

      <ArrayInput source="additionalImages" label="Additional Images">
        <SimpleFormIterator>
          <ImageInput source="src" label="Image" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
          <TextInput source="title" label="Image Title" />
        </SimpleFormIterator>
      </ArrayInput>

      <TextInput source="summary" fullWidth />
      <RichTextInput source="description" label="Event Description" fullWidth />
      <Divider sx={{ width: "100%" }} />
      <TextInput
        source="location.name"
        label="Location Name"
        defaultValue={"The Cola Lounge"}
      />
      <TextInput
        source="location.address"
        label="Location Address"
        defaultValue={"2834 Cherokee Street, 63118"}
      />
      <Box
        sx={{
          p: 2,
          mt: 2,
          mb: 2,
          width: "100%",
          border: "1px solid #ccc",
          borderRadius: "5px",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tickets
        </Typography>
        <ArrayInput source="tickets" label="">
          <SimpleFormIterator>
            <TextInput source="id" label="Ticket ID" />
            <TextInput source="name" label="Ticket Name" />
            <TextInput source="description" label="Ticket Description" />
            <NumberInput source="amount" label="Price in cents" />
            <TextInput source="productType" label="" defaultValue={"ticket"} />
          </SimpleFormIterator>
        </ArrayInput>
      </Box>
      <DateInput source="createdate" defaultValue={new Date()} disabled />
      <DateInput source="lastupdate" defaultValue={new Date()} disabled />
    </SimpleForm>
  </Create>
);

export const EventEdit = (props: any) => {
  return (
    <Edit {...props} transform={transformEventData}>
      <SimpleForm sanitizeEmptyValues>
        <TextInput disabled source="id" label="Event Slug" />
        <BooleanInput source="isActive" />
        <TextInput source="name" label="Event Name" fullWidth />
        {/* Main Image */}
        <ImageInput source="mainImage" label="Main Image" accept="image/*">
          <ImageField source="src" title="title" />
        </ImageInput>

        <ArrayInput source="additionalImages" label="Additional Images">
          <SimpleFormIterator>
            <ImageInput source="src" label="Image" accept="image/*">
              <ImageField source="src" title="title" />
            </ImageInput>
            <TextInput source="title" label="Image Title" />
          </SimpleFormIterator>
        </ArrayInput>

        <TextInput source="summary" fullWidth />
        <RichTextInput
          source="description"
          label="Event Description"
          defaultValue=""
          fullWidth
        />
        <TextInput source="location.name" label="Location Name" fullWidth />
        <TextInput
          source="location.address"
          label="Location Address"
          fullWidth
        />
        <Box
          sx={{
            p: 2,
            mt: 2,
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Instances
          </Typography>
          <Divider sx={{ width: "100%" }} />
          <ReferenceManyField reference="eventInstances" target="eventId">
            <Box>
              <Datagrid>
                <DateField source="startDate" label="Starts At" showTime />
                <DateField source="endDate" label="Ends At" showTime />
                <TextField source="description" label="Description" />
                <EditButton />
              </Datagrid>
              <CreateEventInstanceButton />
            </Box>
          </ReferenceManyField>
        </Box>
        <Box
          sx={{
            p: 2,
            mt: 2,
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Tickets
          </Typography>
          <TextInput source="ticketlink" label="Ticket Link" fullWidth />

          <ArrayInput source="tickets" label="">
            <SimpleFormIterator inline>
              <TextInput source="id" label="Ticket ID" />
              <TextInput source="name" label="Ticket Name" />
              <TextInput source="description" label="Ticket Description" />
              <NumberInput source="amount" label="Price" />
              <TextInput source="productType" defaultValue="ticket" disabled />
            </SimpleFormIterator>
          </ArrayInput>
        </Box>
        <Divider sx={{ width: "100%" }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <DateInput disabled source="createdate" />
          </Grid>
          <Grid item xs={6}>
            <DateInput disabled source="lastupdate" />
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};

const EventsAdmin = {
  list: EventList,
  show: EventShow,
  create: EventCreate,
  edit: EventEdit,
};
export default EventsAdmin;
